import { useEffect, useMemo } from "react"
import type { Monaco } from "@monaco-editor/react"
import type { editor } from "monaco-editor"
import { useLayerDriftQuery } from "@/v2-backoffice-client/layer/drift/layer.drift.query"
import { MAP_LAYER_MONITOR_SOURCE } from "@/v2-backoffice-client/layer/layer.constants"
import { escapeForRegex } from "@/v2-backoffice-client/layer/drift/resolve/layer.drift.resolve"
import {
  getDriftFindings,
  type LayerDriftFinding,
} from "@/v2-backoffice-client/layer/drift/diagnostics/layer.drift.diagnostics"
import type { LayerEditorField } from "@/v2-backoffice-client/layer/update/schema/layer.update.schema"

/**
 * Drift findings for one document.
 *
 * `cache-first` on purpose: the Drift tab badge already fetches this on
 * every sheet open, so reading it here costs nothing. An editor tab is
 * also the wrong place to trigger a refetch — the operator is typing,
 * and drift only changes when the cron re-probes.
 */
export function useLayerDriftFindings(
  featureCode: string | null | undefined,
  field: LayerEditorField,
): LayerDriftFinding[] {
  const { data } = useLayerDriftQuery({
    skip: !featureCode,
    fetchPolicy: "cache-first",
    variables: {
      input: {
        source: MAP_LAYER_MONITOR_SOURCE,
        identifier: String(featureCode ?? ""),
      },
    },
  })

  const entries = data?.getLayerDriftDetail?.Entries
  return useMemo(() => getDriftFindings(entries ?? [], field), [entries, field])
}

/** Marker owner, so clearing ours never wipes Monaco's schema markers. */
const DRIFT_MARKER_OWNER = "layer-drift"

export type UseLayerDriftMarkersInput = {
  editor: editor.IStandaloneCodeEditor | null | undefined
  monaco: Monaco | null | undefined
  findings: readonly LayerDriftFinding[]
  /** Re-runs the match as the operator types. */
  draft: string
}

/**
 * Underlines the drifted tokens in the document itself.
 *
 * This is the part that stops drift feeling like a separate report. The
 * operator editing a Configuration sees a squiggle under the field the
 * service dropped, with the explanation on hover and a tick in the
 * minimap — the same way they already see a schema violation — instead of
 * having to remember that a Drift tab exists.
 *
 * Registered under our own owner so Monaco's JSON-schema markers survive
 * alongside, and recomputed against the live text so a marker clears the
 * moment its token is edited away.
 */
export function useLayerDriftMarkers(input: UseLayerDriftMarkersInput) {
  const { editor: instance, monaco, findings, draft } = input

  useEffect(() => {
    const model = instance?.getModel()
    if (!monaco || !model) return

    const markers = findings.flatMap((finding) =>
      model
        .findMatches(
          `\\b${escapeForRegex(finding.token)}\\b`,
          false,
          true,
          true,
          null,
          false,
        )
        .map((match) => ({
          startLineNumber: match.range.startLineNumber,
          startColumn: match.range.startColumn,
          endLineNumber: match.range.endLineNumber,
          endColumn: match.range.endColumn,
          severity:
            finding.severity === "error"
              ? monaco.MarkerSeverity.Error
              : monaco.MarkerSeverity.Warning,
          message: finding.message,
          source: "drift",
        })),
    )

    monaco.editor.setModelMarkers(model, DRIFT_MARKER_OWNER, markers)

    return () => {
      if (!model.isDisposed()) {
        monaco.editor.setModelMarkers(model, DRIFT_MARKER_OWNER, [])
      }
    }
  }, [instance, monaco, findings, draft])
}
